import type { GUID } from "@faro-lotv/foundation";
import { clearStore } from "@faro-lotv/project-source";
import type { BoundingBox } from "@faro-lotv/service-wires";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

export type BoxMap = Record<GUID, BoundingBox | undefined>;
export type ElementBoxMap = Record<GUID, GUID[] | undefined>;

export type AiState = {
  /** All AI-detected bounding boxes, keyed by their bounding box ID. */
  boundingBoxes: BoxMap;

  /** Maps each element ID to the IDs of its bounding boxes. */
  elementBoxMap: ElementBoxMap;

  /** Whether the AI chat panel is currently open. */
  isChatOpen: boolean;

  /** Whether all bounding boxes of the active element are shown (the "show all" toggle). */
  showBoundingBoxes: boolean;

  /**
   * ID of a single bounding box to show in isolation (e.g. the one the user
   * clicked on in the chat), independent of the `showBoundingBoxes` toggle.
   */
  focusedBoundingBoxId: GUID | undefined;
};

export const initialState: Readonly<AiState> = Object.freeze({
  boundingBoxes: {},
  elementBoxMap: {},
  isChatOpen: false,
  showBoundingBoxes: false,
  focusedBoundingBoxId: undefined,
});

const aiSlice = createSlice({
  name: "ai",
  initialState,
  reducers: {
    addBoundingBox(state, { payload: box }: PayloadAction<BoundingBox>) {
      addBoundingBoxToStore(state, box);
    },

    addBoundingBoxes(state, { payload: boxes }: PayloadAction<BoundingBox[]>) {
      for (const box of boxes) {
        addBoundingBoxToStore(state, box);
      }
    },

    /**
     * Set the visibility of the AI chat
     *
     * @param state Current state
     * @param action The target state of the AI chat panel in mode
     */
    setIsAiChatOpen(state, action: PayloadAction<boolean>) {
      state.isChatOpen = action.payload;
    },

    /**
     * Set the visibility of bounding boxes
     *
     * @param state Current state
     * @param action The target state of bounding boxes visibility
     */
    setShowBoundingBoxes(state, action: PayloadAction<boolean>) {
      state.showBoundingBoxes = action.payload;
    },

    /**
     * Focus a single bounding box so it is shown in isolation, or clear the
     * focus by passing `undefined`.
     *
     * @param state Current state
     * @param action The ID of the bounding box to focus, or `undefined` to clear
     */
    setFocusedBoundingBox(state, action: PayloadAction<GUID | undefined>) {
      state.focusedBoundingBoxId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(clearStore, () => initialState);
  },
});

export const aiSliceReducer = aiSlice.reducer;

export const {
  addBoundingBox,
  addBoundingBoxes,
  setIsAiChatOpen,
  setShowBoundingBoxes,
  setFocusedBoundingBox,
} = aiSlice.actions;

function addBoundingBoxToStore(state: AiState, box: BoundingBox): void {
  const { elementId, boundingBoxId } = box;

  if (!state.elementBoxMap[elementId]) {
    state.elementBoxMap[elementId] = [];
  }

  // This box is already in the store
  if (state.elementBoxMap[elementId].includes(boundingBoxId)) {
    return;
  }

  state.elementBoxMap[elementId].push(boundingBoxId);

  state.boundingBoxes[boundingBoxId] = box;
}
